import React from 'react';
import classNames from 'classnames/bind';

import type { TrackableItem, TrackableList } from 'types/trackables';

import { FILES_SEARCH_ICONS_MAP } from 'files/services/icons_map';
import { formatPretty } from 'services/string';
import { getTrackableDisplays, splitTrackableDisplays } from 'trackables/utils';

import { Icon } from 'components/Icon/IconDeprecated';
import { Pill } from 'components/Pill/Pill';
import { Text } from 'components/Text/Text';
import { Tooltip } from 'components/Tooltip/Tooltip';

import styles from './FileSearchMetadata.module.scss';

const c = classNames.bind(styles);

type MetadataSection = 'objects' | 'events' | 'texts' | 'trackables';

type TrackableItemsLookup = Record<
  string,
  Pick<TrackableItem, 'id' | 'name' | 'trackable_list_id'>
>;
type TrackableListsLookup = Record<string, Pick<TrackableList, 'id' | 'name'>>;

type SectionClassNames = Partial<Record<MetadataSection, string>>;

export type FileSearchMetadataProps = {
  objects?: string[] | null;
  events?: string[] | null;
  texts?: string[] | null;
  trackables?: string[] | null;
  trackableItemsLookup?: TrackableItemsLookup;
  trackableListsLookup?: TrackableListsLookup;
  layout?: 'compact' | 'detail';
  className?: string;
  sectionClassNames?: SectionClassNames;
  withoutContainer?: boolean;
  maxObjectIcons?: number;
  maxEventPills?: number;
};

export function FileSearchMetadata({
  objects,
  events,
  texts,
  trackables,
  trackableItemsLookup,
  trackableListsLookup,
  layout = 'compact',
  className,
  sectionClassNames,
  withoutContainer = false,
  maxObjectIcons = 6,
  maxEventPills = 2,
}: FileSearchMetadataProps) {
  const normalizedObjects = normalizeMetadataValues(objects);
  const normalizedEvents = normalizeMetadataValues(events);
  const normalizedTexts = normalizeMetadataValues(texts);
  const normalizedTrackables = normalizeMetadataValues(trackables);

  const groupedObjects = React.useMemo(
    () => groupObjectsByBase(normalizedObjects),
    [normalizedObjects]
  );

  const trackableDisplays = React.useMemo(
    () =>
      getTrackableDisplays(
        normalizedTrackables,
        trackableItemsLookup,
        trackableListsLookup
      ),
    [normalizedTrackables, trackableItemsLookup, trackableListsLookup]
  );

  const hasMetadata =
    normalizedObjects.length > 0 ||
    normalizedEvents.length > 0 ||
    normalizedTexts.length > 0 ||
    trackableDisplays.length > 0;

  if (!hasMetadata) {
    return null;
  }

  if (layout === 'detail') {
    return (
      <div className={c('metadata', 'detail', className)}>
        {groupedObjects.length > 0 && (
          <div className={c('detailRow', sectionClassNames?.objects)}>
            <Icon name="model" className={c('detailIcon')} />
            <div className={c('detailValues')}>
              {groupedObjects.map(({ base, attributes, hasIcon }) => {
                const icon = FILES_SEARCH_ICONS_MAP[base];
                const tooltipContent =
                  attributes.length > 0
                    ? `${formatPretty(base)}: ${attributes.map(formatPretty).join(', ')}`
                    : formatPretty(base);

                return hasIcon ? (
                  <Tooltip key={base} content={tooltipContent}>
                    <div className={c('objectWithAttributes')}>
                      <div className={c('detailObjectIcon')}>
                        <Icon name={icon} />
                      </div>
                      {attributes.length > 0 && (
                        <div className={c('attributesList')}>
                          {attributes.map((attr) => (
                            <Pill key={attr} size="small" className={c('attributePill')}>
                              {formatPretty(attr)}
                            </Pill>
                          ))}
                        </div>
                      )}
                    </div>
                  </Tooltip>
                ) : (
                  <Tooltip key={base} content={tooltipContent}>
                    <Pill size="small">
                      {formatPretty(base)}
                      {attributes.length > 0 && ` (${attributes.map(formatPretty).join(', ')})`}
                    </Pill>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        )}

        {normalizedEvents.length > 0 && (
          <div className={c('detailRow', sectionClassNames?.events)}>
            <Icon name="alarm" className={c('detailIcon')} />
            <div className={c('detailValues')}>
              {normalizedEvents.map((event) => (
                <Pill key={event} size="small">
                  {formatPretty(event)}
                </Pill>
              ))}
            </div>
          </div>
        )}

        {trackableDisplays.length > 0 && (
          <div className={c('detailRow', sectionClassNames?.trackables)}>
            <Icon name="tag" className={c('detailIcon')} />
            <div className={c('detailValues')}>
              {trackableDisplays.map(({ id, inlineLabel, tooltip }) => (
                <Tooltip key={id} content={tooltip}>
                  <Pill size="small">{inlineLabel}</Pill>
                </Tooltip>
              ))}
            </div>
          </div>
        )}

        {normalizedTexts.length > 0 && (
          <div className={c('detailRow', sectionClassNames?.texts)}>
            <Icon name="clipboard" className={c('detailIcon')} />
            <div className={c('detailValues')}>
              {normalizedTexts.map((text) => (
                <Text key={text} size="small" className={c('detailTextItem')}>
                  {formatPretty(text)}
                </Text>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const objectsWithIcons = groupedObjects.filter((obj) => obj.hasIcon);
  const objectsWithoutIcons = groupedObjects.filter((obj) => !obj.hasIcon);
  const visibleObjectsWithIcons = objectsWithIcons.slice(0, maxObjectIcons);
  const remainingObjectsWithIcons = objectsWithIcons.slice(maxObjectIcons);

  const previewEvents = normalizedEvents.slice(0, maxEventPills);
  const remainingEvents = normalizedEvents.slice(maxEventPills);

  const { primary: primaryTrackable, extras: extraTrackables } =
    splitTrackableDisplays(trackableDisplays);

  const sections = (
    <>
      {groupedObjects.length > 0 && (
        <div className={c('section', 'objects', sectionClassNames?.objects)}>
          {visibleObjectsWithIcons.map(({ base, attributes }) => {
            const tooltipContent =
              attributes.length > 0
                ? `${formatPretty(base)}: ${attributes.map(formatPretty).join(', ')}`
                : formatPretty(base);

            return (
              <Tooltip key={base} content={tooltipContent}>
                <Icon name={FILES_SEARCH_ICONS_MAP[base]} />
              </Tooltip>
            );
          })}

          {(remainingObjectsWithIcons.length > 0 || objectsWithoutIcons.length > 0) && (
            <Tooltip
              content={[
                ...remainingObjectsWithIcons.map(({ base, attributes }) =>
                  attributes.length > 0
                    ? `${formatPretty(base)} (${attributes.map(formatPretty).join(', ')})`
                    : formatPretty(base)
                ),
                ...objectsWithoutIcons.map(({ base, attributes }) =>
                  attributes.length > 0
                    ? `${formatPretty(base)} (${attributes.map(formatPretty).join(', ')})`
                    : formatPretty(base)
                ),
              ].join(', ')}
            >
              <span className={c('more')}>
                +{remainingObjectsWithIcons.length + objectsWithoutIcons.length}
              </span>
            </Tooltip>
          )}
        </div>
      )}

      {normalizedEvents.length > 0 && (
        <div className={c('section', sectionClassNames?.events)}>
          <Icon name="alarm" />
          {previewEvents.map((event) => (
            <Pill key={event} size="small">
              {formatPretty(event)}
            </Pill>
          ))}
          {remainingEvents.length > 0 && (
            <Tooltip
              content={remainingEvents
                .map((event) => formatPretty(event))
                .join(', ')}
            >
              <span className={c('badge')}>+{remainingEvents.length}</span>
            </Tooltip>
          )}
        </div>
      )}

      {normalizedTexts.length > 0 && (
        <div className={c('section', sectionClassNames?.texts)}>
          <Icon name="clipboard" />
          <Tooltip
            content={normalizedTexts.map((text) => formatPretty(text)).join(', ')}
          >
            <Text size="small" className={c('textsInner')}>
              {formatPretty(normalizedTexts.join(', '))}
            </Text>
          </Tooltip>
        </div>
      )}

      {primaryTrackable && (
        <div className={c('section', sectionClassNames?.trackables)}>
          <Icon name="tag" />
          <span className={c('trackablesInner')}>
            <Tooltip content={primaryTrackable.tooltip}>
              <Text size="small" inline className={c('trackablesFirst')}>
                {primaryTrackable.inlineLabel}
              </Text>
            </Tooltip>
            {extraTrackables.length > 0 && (
              <Tooltip
                content={
                  <div className={c('tooltipList')}>
                    {extraTrackables.map(({ id, inlineLabel }) => (
                      <div key={id}>{inlineLabel}</div>
                    ))}
                  </div>
                }
              >
                <span className={c('badge')}>+{extraTrackables.length}</span>
              </Tooltip>
            )}
          </span>
        </div>
      )}
    </>
  );

  if (withoutContainer) {
    return sections;
  }

  return <div className={c('metadata', 'compact', className)}>{sections}</div>;
}

function normalizeMetadataValues(values?: string[] | null): string[] {
  if (Array.isArray(values)) {
    return values.filter((value): value is string => typeof value === 'string');
  }

  if (typeof values === 'string' && values) {
    return [values];
  }

  return [];
}

type ParsedObject = {
  base: string;
  attributes: string[];
};

function parseObjectWithAttributes(object: string): ParsedObject {
  const parts = object.split('.');
  return {
    base: parts[0],
    attributes: parts.slice(1),
  };
}

type GroupedObject = {
  base: string;
  attributes: string[];
  hasIcon: boolean;
};

function groupObjectsByBase(objects: string[]): GroupedObject[] {
  const groups = new Map<string, Set<string>>();

  for (const object of objects) {
    const { base, attributes } = parseObjectWithAttributes(object);
    if (!groups.has(base)) {
      groups.set(base, new Set());
    }
    for (const attr of attributes) {
      groups.get(base)!.add(attr);
    }
  }

  return Array.from(groups.entries()).map(([base, attrs]) => ({
    base,
    attributes: Array.from(attrs),
    hasIcon: !!FILES_SEARCH_ICONS_MAP[base],
  }));
}
